import React, { useState, useEffect } from 'react';
import { Mic, Check, Clock, Shield, Zap, Users, TrendingUp, ChevronDown, ChevronUp, Play, X } from 'lucide-react';
import VoiceAnimationDemo from '../components/VoiceAnimationDemo';
import SectionNav from '../components/SectionNav';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import { toast } from 'sonner';
import { voiceCommands, mockResults, problemSteps, riskTiers, auditTrailData, testimonials, features, faqs, partners } from '../mock';

const LandingPage = () => {
  const [voiceInput, setVoiceInput] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [selectedProblemStep, setSelectedProblemStep] = useState(0);
  const [activeTab, setActiveTab] = useState('routine');
  const [selectedAudit, setSelectedAudit] = useState(null);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [employees, setEmployees] = useState(500);
  const [tickets, setTickets] = useState(1000);
  const [avgTime, setAvgTime] = useState(15);
  const [showWaitlistModal, setShowWaitlistModal] = useState(false);
  const [waitlistData, setWaitlistData] = useState({ name: '', email: '', company: '', size: '' });

  // Auto-advance problem steps
  useEffect(() => {
    const timer = setInterval(() => {
      setSelectedProblemStep((prev) => (prev + 1) % problemSteps.length);
    }, 3000);
    return () => clearInterval(timer);
  }, []);

  // Auto-advance testimonials
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const handleVoiceCommand = () => {
    if (!voiceInput.trim()) return;
    
    setIsProcessing(true);
    setResult(null);

    setTimeout(() => {
      const mockResult = mockResults[voiceInput] || {
        task: 'Task Completed',
        details: 'Your request has been processed successfully',
        timeSaved: '2m 30s → 5s',
        auditTrail: 'View full audit trail',
        status: 'success'
      };
      setResult(mockResult);
      setIsProcessing(false);
    }, 1500);
  };

  const handleTryAgain = () => {
    setVoiceInput('');
    setResult(null);
  };

  const scrollToDemo = () => {
    document.getElementById('video-demo')?.scrollIntoView({ behavior: 'smooth' });
  };

  const calculateROI = () => {
    const currentAnnualCost = (tickets * 12 * avgTime * 0.5);
    const deflectionRate = 0.7;
    const savings = currentAnnualCost * deflectionRate;
    const auraAnnualCost = 15000 + (employees * 20);
    const netROI = savings - auraAnnualCost;
    const paybackMonths = auraAnnualCost / (savings / 12);

    return {
      currentCost: Math.round(currentAnnualCost),
      savings: Math.round(savings),
      auraInvestment: auraAnnualCost,
      netROI: Math.round(netROI),
      paybackMonths: Math.round(paybackMonths * 10) / 10
    };
  };

  const roi = calculateROI();

  const handleWaitlistSubmit = (e) => {
    e.preventDefault();
    
    // Store in localStorage
    const existingWaitlist = JSON.parse(localStorage.getItem('auraWaitlist') || '[]');
    existingWaitlist.push({ ...waitlistData, timestamp: new Date().toISOString() });
    localStorage.setItem('auraWaitlist', JSON.stringify(existingWaitlist));

    toast.success('You\'re on the waitlist!', {
      description: 'We\'ll be in touch soon with next steps.'
    });

    setShowWaitlistModal(false);
    setWaitlistData({ name: '', email: '', company: '', size: '' });
  };

  return (
    <div className="landing-page">
      {/* Section Navigation Helper */}
      <SectionNav />
      
      {/* Hero Section */}
      <section className="hero-gradient min-h-screen flex items-center justify-center px-4 pt-24 pb-12">
        <div className="max-w-4xl mx-auto text-center">
          <div className="mb-6 inline-flex items-center gap-2 bg-white/40 backdrop-blur-sm rounded-full px-4 py-2 border border-black/10">
            <Zap className="w-4 h-4" />
            <span className="mono-text text-xs">LAUNCHING Q2 2026</span>
          </div>
          
          <h1 className="heading-hero mb-6">
            HR tasks in 60 seconds.<br />Not 60 clicks.
          </h1>
          
          <p className="body-large mb-8 max-w-2xl mx-auto text-text-secondary">
            Aura is the AI agent that turns "Submit PTO request" into a single voice command—with full audit trails and governance built in.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Button onClick={() => setShowWaitlistModal(true)} className="btn-primary text-base px-8 py-6">
              Join the Waitlist
            </Button>
            <Button onClick={scrollToDemo} variant="outline" className="btn-secondary text-base px-8 py-6">
              See How It Works
            </Button>
          </div>

          {/* Animated Voice Demo Mockup */}
          <Card className="voice-card max-w-3xl mx-auto mt-12">
            <CardHeader>
              <CardTitle className="heading-3 text-left">Use your voice</CardTitle>
              <CardDescription className="body-small text-left">
                Aura seamlessly integrates with your daily workflow tools. Use voice commands directly in Microsoft Teams and Slack to manage HR tasks without switching apps.
              </CardDescription>
              <div className="flex items-center gap-4 mt-4">
                <span className="caption text-text-muted">Works with:</span>
                <div className="flex items-center gap-4">
                  <div className="bg-white p-3 rounded-lg border border-border-light hover:shadow-md transition-all">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_aura-converse/artifacts/evhzn2uk_image.png" 
                      alt="Microsoft Teams" 
                      className="w-8 h-8"
                    />
                  </div>
                  <div className="bg-white p-3 rounded-lg border border-border-light hover:shadow-md transition-all">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_aura-converse/artifacts/qi4ak8wc_image.png" 
                      alt="Slack" 
                      className="w-8 h-8"
                    />
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <VoiceAnimationDemo />
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Our Story Section */}
      <section id="our-story" className="py-20 px-4 bg-bg-section">
        <div className="max-w-4xl mx-auto">
          <h2 className="heading-1 text-center mb-12">Our Story</h2>
          
          <Card className="voice-card accent-orange">
            <CardContent className="pt-8 pb-8">
              <p className="body-large leading-relaxed text-text-primary">
                It started with something embarrassingly simple: filling out timesheets and applying for leave at our old company took more energy than doing the actual work. We had to update Workday and send a separate email to our managers, so approvals went missing, audits broke down, and HR would chase us months later for things we barely remembered.
              </p>
              <p className="body-large leading-relaxed text-text-primary mt-6">
                When we were exiting the company, the chaos got worse - a different off-boarding tool, multiple reminders, and a loop of HR × Manager × Employee that felt completely avoidable.
              </p>
              <p className="body-large leading-relaxed text-text-primary mt-6">
                At some point, we realised: <span className="font-semibold">none of this should need human babysitting. A single agent should be able to handle it.</span>
              </p>
              <div className="mt-8 pt-6 border-t border-border-light text-center">
                <p className="body-small text-text-muted italic">That's why we built Aura.</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Problem Section */}
      <section id="product" className="py-20 px-4 bg-bg-page">
        <div className="max-w-6xl mx-auto">
          <h2 className="heading-1 text-center mb-4">Remember the last time you needed to...</h2>
          <p className="body-large text-center mb-12 text-text-secondary">The traditional HR workflow</p>

          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div className="space-y-4">
              {problemSteps.map((step, idx) => (
                <div
                  key={idx}
                  className={`p-4 rounded-lg border transition-all cursor-pointer ${
                    selectedProblemStep === idx
                      ? 'bg-accent-pink-200 border-accent-orange-400'
                      : 'bg-white border-border-light hover:border-border-primary'
                  }`}
                  onClick={() => setSelectedProblemStep(idx)}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-text-primary text-white flex items-center justify-center flex-shrink-0 caption font-semibold">
                      {step.step}
                    </div>
                    <div className="flex-1">
                      <h4 className="heading-3 text-sm mb-1">{step.title}</h4>
                      <p className="caption text-text-muted">{step.description}</p>
                    </div>
                    <span className="mono-text text-xs">{step.time}</span>
                  </div>
                </div>
              ))}
              <div className="text-center pt-4 border-t border-border-light">
                <p className="heading-2 text-red-600">Total Time: 3m 47s</p>
              </div>
            </div>

            <div className="bg-accent-green-200 rounded-lg p-8 text-center">
              <h3 className="heading-2 mb-4">With Aura:</h3>
              <div className="mb-6">
                <div className="inline-flex items-center gap-3 bg-white rounded-full px-6 py-3 mb-4">
                  <Mic className="w-5 h-5" />
                  <span className="body-medium">"Submit my expense"</span>
                </div>
              </div>
              <div className="w-16 h-16 rounded-full bg-green-600 flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-white" />
              </div>
              <p className="heading-2 text-green-700 mb-2">Done</p>
              <p className="heading-1 text-green-600">8 seconds</p>
            </div>
          </div>
        </div>
      </section>

      {/* Video Demo Section */}
      <section id="video-demo" className="py-20 px-4 bg-bg-section">
        <div className="max-w-6xl mx-auto">
          <h2 className="heading-1 text-center mb-4">See Aura in Action</h2>
          <p className="body-large text-center mb-12 text-text-secondary">Watch how Aura transforms this 3-minute process into seconds</p>
          
          <div className="max-w-4xl mx-auto">
            <Card className="voice-card overflow-hidden">
              <CardContent className="p-0">
                <video 
                  controls 
                  className="w-full"
                  poster=""
                >
                  <source src="https://customer-assets.emergentagent.com/job_aura-converse/artifacts/2ayrh9vp_Prototype_video.mp4" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-20 px-4 bg-bg-page">
        <div className="max-w-6xl mx-auto">
          <h2 className="heading-1 text-center mb-4">How Aura Works</h2>
          <p className="body-large text-center mb-12 text-text-secondary">Risk-tiered autonomy ensures safety at every level</p>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-3 mb-8 bg-white border border-border-light p-1 rounded-lg h-auto">
              {riskTiers.map((tier) => (
                <TabsTrigger 
                  key={tier.id} 
                  value={tier.id}
                  className="data-[state=active]:bg-text-primary data-[state=active]:text-white rounded-md py-3"
                >
                  <div>
                    <div className="heading-3 text-sm">{tier.title}</div>
                    <div className="caption">{tier.description}</div>
                  </div>
                </TabsTrigger>
              ))}
            </TabsList>

            {riskTiers.map((tier) => (
              <TabsContent key={tier.id} value={tier.id}>
                <div className="grid md:grid-cols-2 gap-6">
                  {tier.examples.map((example, idx) => (
                    <Card key={idx} className={`voice-card ${tier.color}`}>
                      <CardContent className="pt-6">
                        <div className="mb-4">
                          <div className="flex items-start gap-2 mb-3">
                            <Users className="w-5 h-5 mt-1 flex-shrink-0" />
                            <p className="body-medium font-medium">{example.query}</p>
                          </div>
                        </div>
                        <div className="bg-white/60 rounded-lg p-4">
                          <div className="flex items-start gap-2">
                            {example.instant ? (
                              <Zap className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                            ) : (
                              <Shield className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" />
                            )}
                            <p className="body-small">{example.response}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </section>

      {/* Trust & Governance Section */}
      <section className="py-20 px-4 bg-bg-page">
        <div className="max-w-6xl mx-auto">
          <h2 className="heading-1 text-center mb-4">Complete Audit Trail & Governance</h2>
          <p className="body-large text-center mb-12 text-text-secondary">Every action is logged, traceable, and reversible</p>

          <Card className="voice-card mb-8">
            <CardContent className="pt-6">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border-light">
                      <th className="text-left py-3 px-4 caption font-semibold">TIME</th>
                      <th className="text-left py-3 px-4 caption font-semibold">USER</th>
                      <th className="text-left py-3 px-4 caption font-semibold">ACTION</th>
                      <th className="text-left py-3 px-4 caption font-semibold">CONFIDENCE</th>
                      <th className="text-left py-3 px-4 caption font-semibold">STATUS</th>
                      <th className="text-left py-3 px-4 caption font-semibold">DETAILS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditTrailData.map((audit) => (
                      <tr key={audit.id} className="border-b border-border-light hover:bg-bg-section transition-colors">
                        <td className="py-3 px-4 mono-text text-xs">{audit.time}</td>
                        <td className="py-3 px-4 body-small">{audit.user}</td>
                        <td className="py-3 px-4 body-small">{audit.action}</td>
                        <td className="py-3 px-4 body-small">{audit.confidence}</td>
                        <td className="py-3 px-4">
                          <span className="inline-flex items-center gap-1 bg-accent-green-200 px-2 py-1 rounded-full caption">
                            <Check className="w-3 h-3" />
                            {audit.status}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <button
                            onClick={() => setSelectedAudit(audit)}
                            className="caption text-blue-600 underline hover:text-blue-800"
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {selectedAudit && (
            <Dialog open={!!selectedAudit} onOpenChange={() => setSelectedAudit(null)}>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle className="heading-2">Audit Trail Details</DialogTitle>
                  <DialogDescription className="body-small">Complete transaction log for {selectedAudit.action}</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div>
                    <h4 className="heading-3 text-sm mb-2">What the user asked:</h4>
                    <p className="body-small bg-bg-section p-3 rounded-lg">"{selectedAudit.details.userQuery}"</p>
                  </div>
                  <div>
                    <h4 className="heading-3 text-sm mb-2">How Aura interpreted it:</h4>
                    <p className="body-small bg-bg-section p-3 rounded-lg">{selectedAudit.details.interpretation}</p>
                  </div>
                  <div>
                    <h4 className="heading-3 text-sm mb-2">Policies checked:</h4>
                    <ul className="body-small space-y-1">
                      {selectedAudit.details.policiesChecked.map((policy, idx) => (
                        <li key={idx} className="flex items-center gap-2">
                          <Check className="w-4 h-4 text-green-600" />
                          {policy}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h4 className="heading-3 text-sm mb-2">Action taken:</h4>
                    <p className="body-small bg-accent-green-200 p-3 rounded-lg">{selectedAudit.details.actionTaken}</p>
                  </div>
                  {selectedAudit.details.undoOption && (
                    <Button variant="outline" className="btn-secondary w-full">
                      Undo This Action
                    </Button>
                  )}
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </section>

      {/* Before/After Comparison */}
      <section className="py-20 px-4 bg-bg-section">
        <div className="max-w-6xl mx-auto">
          <h2 className="heading-1 text-center mb-12">The Difference is Clear</h2>

          <div className="grid md:grid-cols-2 gap-8">
            <Card className="voice-card">
              <CardHeader>
                <CardTitle className="heading-2 text-red-600">Traditional HR System</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <X className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                    <span className="body-small">Login to multiple systems</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <X className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                    <span className="body-small">Navigate complex menus</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <X className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                    <span className="body-small">Fill out multiple forms</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <X className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                    <span className="body-small">Wait for confirmations</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <X className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                    <span className="body-small">Check back for status</span>
                  </li>
                </ul>
                <div className="mt-6 pt-6 border-t border-border-light">
                  <p className="heading-2 text-red-600">Average: 3-5 minutes per task</p>
                </div>
              </CardContent>
            </Card>

            <Card className="voice-card accent-green">
              <CardHeader>
                <CardTitle className="heading-2 text-green-700">With Aura</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                    <span className="body-small">One voice/chat command</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                    <span className="body-small">Instant processing</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                    <span className="body-small">Automatic form completion</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                    <span className="body-small">Real-time confirmations</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                    <span className="body-small">Complete audit trail</span>
                  </li>
                </ul>
                <div className="mt-6 pt-6 border-t border-border-light">
                  <p className="heading-2 text-green-700">Average: 8-15 seconds per task</p>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="mt-8">
            <Card className="voice-card">
              <CardContent className="pt-6">
                <h3 className="heading-2 mb-6 text-center">Feature Comparison</h3>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border-light">
                        <th className="text-left py-3 px-4 heading-3 text-sm">Feature</th>
                        <th className="text-center py-3 px-4 heading-3 text-sm">Traditional</th>
                        <th className="text-center py-3 px-4 heading-3 text-sm">Aura</th>
                      </tr>
                    </thead>
                    <tbody>
                      {features.map((feature, idx) => (
                        <tr key={idx} className="border-b border-border-light">
                          <td className="py-3 px-4 body-small">{feature.feature}</td>
                          <td className="text-center py-3 px-4">
                            {feature.traditional === true ? (
                              <Check className="w-5 h-5 text-green-600 mx-auto" />
                            ) : feature.traditional === false ? (
                              <X className="w-5 h-5 text-red-600 mx-auto" />
                            ) : (
                              <span className="caption text-text-muted">{feature.traditional}</span>
                            )}
                          </td>
                          <td className="text-center py-3 px-4">
                            {feature.aura === true ? (
                              <Check className="w-5 h-5 text-green-600 mx-auto" />
                            ) : (
                              <X className="w-5 h-5 text-red-600 mx-auto" />
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Social Proof Section */}
      <section className="py-20 px-4 bg-bg-page">
        <div className="max-w-6xl mx-auto">
          <h2 className="heading-1 text-center mb-12">Trusted by HR Leaders</h2>

          <div className="max-w-3xl mx-auto mb-12">
            <Card className="voice-card accent-purple">
              <CardContent className="pt-8 pb-8">
                <div className="text-center">
                  <p className="body-large italic mb-6">"{testimonials[currentTestimonial].quote}"</p>
                  <div>
                    <p className="heading-3">{testimonials[currentTestimonial].author}</p>
                    <p className="body-small text-text-muted">{testimonials[currentTestimonial].role}</p>
                    <p className="caption text-text-muted">{testimonials[currentTestimonial].company}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <div className="flex justify-center gap-2 mt-4">
              {testimonials.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentTestimonial(idx)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    currentTestimonial === idx ? 'bg-text-primary w-6' : 'bg-border-primary'
                  }`}
                />
              ))}
            </div>
          </div>

          <Card className="voice-card accent-blue max-w-2xl mx-auto">
            <CardContent className="pt-6 text-center">
              <TrendingUp className="w-12 h-12 mx-auto mb-4 text-blue-600" />
              <p className="heading-2 mb-2">87% of HR tickets are routine</p>
              <p className="body-small text-text-muted">Yet 60% of HR teams still process them manually</p>
              <p className="caption mt-2 text-text-muted">Source: Industry research, 2024</p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Target Companies Carousel */}
      <section className="py-16 px-4 bg-bg-section">
        <div className="max-w-6xl mx-auto">
          <h3 className="heading-2 text-center mb-12">Built for Enterprise Scale</h3>
          
          <div className="relative overflow-hidden">
            <div className="company-carousel-track">
              {/* First set */}
              <div className="company-logo-item">
                <div className="company-logo-card">
                  <span className="heading-3">JPMorgan Chase</span>
                </div>
              </div>
              <div className="company-logo-item">
                <div className="company-logo-card">
                  <span className="heading-3">Amazon</span>
                </div>
              </div>
              <div className="company-logo-item">
                <div className="company-logo-card">
                  <span className="heading-3">Microsoft</span>
                </div>
              </div>
              <div className="company-logo-item">
                <div className="company-logo-card">
                  <span className="heading-3">TCS</span>
                </div>
              </div>
              <div className="company-logo-item">
                <div className="company-logo-card">
                  <span className="heading-3">Infosys</span>
                </div>
              </div>
              {/* Duplicate set for seamless loop */}
              <div className="company-logo-item">
                <div className="company-logo-card">
                  <span className="heading-3">JPMorgan Chase</span>
                </div>
              </div>
              <div className="company-logo-item">
                <div className="company-logo-card">
                  <span className="heading-3">Amazon</span>
                </div>
              </div>
              <div className="company-logo-item">
                <div className="company-logo-card">
                  <span className="heading-3">Microsoft</span>
                </div>
              </div>
              <div className="company-logo-item">
                <div className="company-logo-card">
                  <span className="heading-3">TCS</span>
                </div>
              </div>
              <div className="company-logo-item">
                <div className="company-logo-card">
                  <span className="heading-3">Infosys</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ROI Calculator Section */}
      <section id="pricing-model" className="py-20 px-4 bg-bg-section">
        <div className="max-w-4xl mx-auto">
          <h2 className="heading-1 text-center mb-4">Calculate Your Savings</h2>
          <p className="body-large text-center mb-12 text-text-secondary">See how much Aura could save your organization</p>

          <Card className="voice-card">
            <CardContent className="pt-6">
              <div className="space-y-6">
                <div>
                  <Label className="body-small mb-2 block">Number of Employees</Label>
                  <Slider
                    value={[employees]}
                    onValueChange={(val) => setEmployees(val[0])}
                    min={100}
                    max={10000}
                    step={100}
                    className="mb-2"
                  />
                  <p className="heading-3 text-right">{employees.toLocaleString()}</p>
                </div>

                <div>
                  <Label className="body-small mb-2 block">Monthly HR Tickets</Label>
                  <Slider
                    value={[tickets]}
                    onValueChange={(val) => setTickets(val[0])}
                    min={100}
                    max={5000}
                    step={50}
                    className="mb-2"
                  />
                  <p className="heading-3 text-right">{tickets.toLocaleString()}</p>
                </div>

                <div>
                  <Label className="body-small mb-2 block">Avg Resolution Time (minutes)</Label>
                  <Slider
                    value={[avgTime]}
                    onValueChange={(val) => setAvgTime(val[0])}
                    min={5}
                    max={30}
                    step={1}
                    className="mb-2"
                  />
                  <p className="heading-3 text-right">{avgTime} minutes</p>
                </div>

                <div className="pt-6 border-t border-border-light">
                  <h3 className="heading-2 mb-4">Your Projected Results</h3>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="bg-red-50 p-4 rounded-lg">
                      <p className="caption text-text-muted mb-1">Current Annual Cost</p>
                      <p className="heading-2 text-red-600">${roi.currentCost.toLocaleString()}</p>
                    </div>
                    <div className="bg-green-50 p-4 rounded-lg">
                      <p className="caption text-text-muted mb-1">Projected Savings (70% deflection)</p>
                      <p className="heading-2 text-green-600">${roi.savings.toLocaleString()}</p>
                    </div>
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <p className="caption text-text-muted mb-1">Aura Investment</p>
                      <p className="heading-2 text-blue-600">${roi.auraInvestment.toLocaleString()}</p>
                    </div>
                    <div className="bg-purple-50 p-4 rounded-lg">
                      <p className="caption text-text-muted mb-1">Net ROI</p>
                      <p className="heading-2 text-purple-600">${roi.netROI.toLocaleString()}</p>
                    </div>
                  </div>
                  <div className="mt-4 text-center">
                    <p className="body-small text-text-muted">Payback period: <span className="font-semibold">{roi.paybackMonths} months</span></p>
                  </div>
                </div>

                <Button onClick={() => setShowWaitlistModal(true)} className="btn-primary w-full">
                  Get Started with These Savings
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-4 bg-bg-page">
        <div className="max-w-3xl mx-auto">
          <h2 className="heading-1 text-center mb-12">Frequently Asked Questions</h2>

          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, idx) => (
              <AccordionItem key={idx} value={`item-${idx}`} className="bg-white border border-border-light rounded-lg px-6">
                <AccordionTrigger className="heading-3 text-sm text-left hover:no-underline">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="body-small text-text-secondary pt-2">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 px-4 bg-accent-purple-200">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="heading-1 mb-4">Ready to Transform Your HR Operations?</h2>
          <p className="body-large mb-8 text-text-secondary">
            Join the waitlist today and be among the first to experience the future of HR automation.
          </p>
          <Button onClick={() => setShowWaitlistModal(true)} className="btn-primary text-base px-12 py-6">
            Join the Waitlist
          </Button>
          <p className="caption mt-4 text-text-muted">Starting at $15,000/year • Money-back guarantee • SOC 2 compliant</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 bg-text-primary text-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="heading-3 text-sm mb-4">Aura</h3>
              <p className="caption">Enterprise-grade AI with SOC 2 compliance</p>
            </div>
            <div>
              <h4 className="heading-3 text-sm mb-4">Product</h4>
              <ul className="space-y-2 caption">
                <li><a href="#" className="hover:underline">Features</a></li>
                <li><a href="#" className="hover:underline">Pricing</a></li>
                <li><a href="#" className="hover:underline">Security</a></li>
              </ul>
            </div>
            <div>
              <h4 className="heading-3 text-sm mb-4">Company</h4>
              <ul className="space-y-2 caption">
                <li><a href="#" className="hover:underline">About</a></li>
                <li><a href="#" className="hover:underline">Blog</a></li>
                <li><a href="#" className="hover:underline">Careers</a></li>
              </ul>
            </div>
            <div>
              <h4 className="heading-3 text-sm mb-4">Legal</h4>
              <ul className="space-y-2 caption">
                <li><a href="#" className="hover:underline">Privacy</a></li>
                <li><a href="#" className="hover:underline">Terms</a></li>
                <li><a href="#" className="hover:underline">Contact</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/20 pt-8 text-center">
            <p className="caption">© 2025 Aura. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Waitlist Modal */}
      <Dialog open={showWaitlistModal} onOpenChange={setShowWaitlistModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="heading-2">Join the Waitlist</DialogTitle>
            <DialogDescription className="body-small">Be among the first to experience Aura</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleWaitlistSubmit} className="space-y-4 mt-4">
            <div>
              <Label htmlFor="name" className="body-small">Full Name</Label>
              <Input
                id="name"
                value={waitlistData.name}
                onChange={(e) => setWaitlistData({ ...waitlistData, name: e.target.value })}
                required
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="email" className="body-small">Work Email</Label>
              <Input
                id="email"
                type="email"
                value={waitlistData.email}
                onChange={(e) => setWaitlistData({ ...waitlistData, email: e.target.value })}
                required
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="company" className="body-small">Company Name</Label>
              <Input
                id="company"
                value={waitlistData.company}
                onChange={(e) => setWaitlistData({ ...waitlistData, company: e.target.value })}
                required
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="size" className="body-small">Company Size</Label>
              <Select value={waitlistData.size} onValueChange={(val) => setWaitlistData({ ...waitlistData, size: val })} required>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select company size" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1-50">1-50 employees</SelectItem>
                  <SelectItem value="51-200">51-200 employees</SelectItem>
                  <SelectItem value="201-500">201-500 employees</SelectItem>
                  <SelectItem value="501-1000">501-1000 employees</SelectItem>
                  <SelectItem value="1000+">1000+ employees</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" className="btn-primary w-full">
              Join Waitlist
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LandingPage;